import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import ItemTile from '../Components/ItemTile/ItemTile';
import makeStyles from '@material-ui/core/styles/makeStyles';
import { Typography, Grid, Box, MenuItem, FormControl, Select } from '@material-ui/core';
import { T } from '@configuratorware/configurator-frontendgui/Framework/i18n';
import { lazyFetch } from '@configuratorware/configurator-frontendgui/App/Utils/Request/lazyFetch';
import { getConf } from '@configuratorware/configurator-frontendgui/App/configuration';
import get from 'lodash/get';
import headerImage from './images/header_image.jpg';
import { getLocale } from '@configuratorware/configurator-frontendgui/Framework/i18n';
import LanguageSwitch from '../Components/LanguageSwitch/LanguageSwitch';

const useStyles = makeStyles(
  (theme) => ({
    container: {
      backgroundColor: 'white',
      display: 'flex',
      flexDirection: 'column',
      padding: 50,
      paddingTop: 30,
      [theme.breakpoints.up('sm')]: {
        padding: 80,
      },
      [theme.breakpoints.up('lg')]: {
        padding: '100px 230px',
        paddingTop: '30px',
      },
    },
    categoryContainer: {
      display: 'flex',
      gap: '15px',
      width: '100%',
      marginTop: '40px',
      marginBottom: '30px',
      overflowX: 'auto',
      [theme.breakpoints.up('lg')]: {
        marginTop: '20px',
      },
    },
    categoryButton: {
      backgroundColor: '#ededed',
      color: '#000',
      borderRadius: '50px',
      padding: '20px',
      '&.active': {
        backgroundColor: theme.palette.primary.main,
        color: '#fff',
      },
    },
    header: {
      position: 'relative',
      height: '150px',
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'center',
      backgroundImage: `linear-gradient(0deg, rgba(0,0,0,0.5466561624649859) 0%, rgba(255,255,255,0) 100%), url("${headerImage}")`,
      backgroundPosition: 'center',
      backgroundSize: 'cover',
      backgroundRepeat: 'no-repeat',
      padding: 50,
      [theme.breakpoints.up('sm')]: {
        padding: 80,
        height: '230px',
      },
      [theme.breakpoints.up('lg')]: {
        padding: '200px 230px',
        height: '400px',
      },
    },
    paginationContainer: {
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      marginBottom: '10px',
    },
    label: {
      backgroundColor: '#A0C513',
      color: 'white',
      padding: 10,
      width: 'fit-content',
      '& p': {
        fontSize: '1rem',
        [theme.breakpoints.up('md')]: {
          fontSize: '1.3rem',
        },
      },
    },
    slogan: {
      color: 'white',
      marginTop: 10,
      fontSize: '1.5rem',
      [theme.breakpoints.up('md')]: {
        width: '70%',
        fontSize: '3rem',
      },
    },
    filterContainer: {
      display: 'flex',
      flexDirection: 'row',
      justifyContent: 'space-between',
      width: '100%',
      alignItems: 'center',
      margin: '25px 0',
      [theme.breakpoints.up('sm')]: {
        justifyContent: 'flex-end',
        margin: '50px 0 10px',
      },
      [theme.breakpoints.up('md')]: {
        margin: '100px 0 10px',
      },
    },
    languageRow: {
      alignSelf: 'flex-end',
    },
    filter: {
      width: '180px',
      height: 45,
      borderRadius: 0,
      marginLeft: 20,
    },
  }),
  { name: 'ItemOverview' }
);

const fetchFilteredItems = async (filter) => {
  const response = await lazyFetch({
    url: `items`,
    params:
      filter === 'all'
        ? {}
        : {
            filters: JSON.stringify({
              'item.attribute.type': filter,
            }),
          },
  });
  return response.data.data;
};

const ItemOverview = ({ pageSize }) => {
  const classes = useStyles();

  const [items, setItems] = useState([]);
  const [pagination, setPagination] = useState({
    count: 0,
    from: 0,
    to: pageSize,
  });
  const HOST = getConf('network.host');

  const LANGUAGE = getLocale();
  let pdfAttribute;
  switch (LANGUAGE) {
    case 'de-DE':
      pdfAttribute = 'pdf_itemlink_de';
      break;
    case 'en-GB':
      pdfAttribute = 'pdf_itemlink_en';
      break;
    default:
      pdfAttribute = 'pdf_itemlink_de';
  }

  useEffect(() => {
    setPagination({ ...pagination, count: Math.ceil(items.length / pageSize) });
  }, [pageSize, items, pagination.from, pagination.to]);

  const [filter, setFilter] = useState('all');

  const handleChangeFilter = (event) => {
    setFilter(event.target.value);
  };

  useEffect(() => {
    (async () => {
      const items = await fetchFilteredItems(filter);
      setItems(items);
    })();
  }, [filter]);

  const getAttributeValue = (item, attribute) => {
    return get(
      item.attributes.find((attr) => attr.identifier === attribute),
      'values[0].translated_value',
      null
    );
  };

  const renderFilter = () => {
    return (
      <FormControl variant="outlined" className={classes.filterContainer}>
        <Typography>{T('itemOverview.filter')}</Typography>
        <Select
          value={filter}
          onChange={handleChangeFilter}
          className={classes.filter}
          MenuProps={{
            disableScrollLock: true,
          }}
        >
          <MenuItem value={'all'}>{T('itemOverview.all')}</MenuItem>
          <MenuItem value={'heavy_load'}>{T('itemOverview.heavyDuty')}</MenuItem>
          <MenuItem value={'universal'}>{T('itemOverview.universal')}</MenuItem>
        </Select>
      </FormControl>
    );
  };
  const renderItems = () => {
    const itemsPerPage = items.slice(pagination.from, pagination.to);

    return (
      <Grid container spacing={3} alignItems="stretch">
        {itemsPerPage.map((item, index) => (
          <Grid item key={item.identifier} xs={12} lg={6}>
            <ItemTile
              title={item.title}
              image={item.detailImage}
              linkTo={`${HOST}/identifier:${item.identifier}${location.search}`}
              key={item.identifier}
              milieu={getAttributeValue(item, 'mileau_headline')}
              abstract={item.abstract}
              price={get(item, 'calculationResult.totalFormatted', null)}
              pdfLink={getAttributeValue(item, pdfAttribute)}
            />
          </Grid>
        ))}
      </Grid>
    );
  };

  const renderNotFound = () => {
    return (
      <Box>
        <Typography>{T('itemOverview.notFound')}</Typography>
      </Box>
    );
  };

  return (
    <>
      <div className={classes.header}>
        <Box className={classes.label}>
          <Typography>{T('itemOverview.our3DConfigurator')}</Typography>
        </Box>
        <Typography variant="h3" className={classes.slogan}>
          {T('itemOverview.yourRequirementsOurBedShort')}
        </Typography>
      </div>

      <Grid container className={classes.container}>
        <Grid item xs={12} className={classes.languageRow}>
          <LanguageSwitch />
        </Grid>
        <Grid container spacing={2} item xs={12}>
          <Grid item xs={12}>
            <Typography variant="h5"> {T('itemOverview.fewClicks')}</Typography>
          </Grid>
          <Grid item xs={12}>
            <Typography>{T('itemOverview.yourRequirementsOurBedLong')}</Typography>
          </Grid>
          <Grid item xs={12} justifyContent={'flex-end'}>
            {renderFilter()}
          </Grid>
          {items.length > 0 ? renderItems() : renderNotFound()}
        </Grid>
      </Grid>
    </>
  );
};

ItemOverview.propTypes = {
  pageSize: PropTypes.number,
};

ItemOverview.defaultProps = {
  pageSize: 8,
};

export default ItemOverview;
